import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { Backdrop, Title, Kicker } from "../components";
import { COLORS, FONT, NEUTRAL, SAFE } from "../theme";

const Card: React.FC<{
  side: "left" | "right";
  accent: string;
  tag: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  delay: number;
}> = ({ side, accent, tag, title, desc, icon, delay }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;
  const from = side === "left" ? -120 : 120;
  const x = interpolate(f, [0, 26], [from, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const op = interpolate(f, [0, 20], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        width: 620,
        padding: 52,
        borderRadius: 28,
        background: NEUTRAL.glassBg,
        border: `1px solid ${NEUTRAL.line}`,
        boxShadow: `0 30px 80px rgba(0,0,0,0.35)`,
        translate: `${x}px 0px`,
        opacity: op,
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          width: 116,
          height: 116,
          borderRadius: 26,
          background: `${accent}22`,
          border: `1px solid ${accent}55`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 34,
        }}
      >
        {icon}
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: 24,
          letterSpacing: 4,
          textTransform: "uppercase",
          color: accent,
          marginBottom: 14,
        }}
      >
        {tag}
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontWeight: 800,
          fontSize: 52,
          color: COLORS.white,
          letterSpacing: -1,
          marginBottom: 16,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: 32,
          lineHeight: 1.35,
          color: COLORS.sub,
        }}
      >
        {desc}
      </div>
    </div>
  );
};

const QrIcon: React.FC = () => (
  <svg width="60" height="60" viewBox="0 0 24 24" fill={COLORS.blueLight}>
    <path d="M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm10-2h2v2h-2v-2Zm4 0h2v2h-2v-2Zm-4 4h2v2h-2v-2Zm4 0h2v4h-4v-2h2v-2Z" />
  </svg>
);

const ObdIcon: React.FC = () => {
  const frame = useCurrentFrame();
  const pulse = interpolate(frame % 40, [0, 40], [0.3, 1]);
  return (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="14" r="3" fill={COLORS.purpleLight} />
      <path
        d="M8.5 10.5a5 5 0 0 1 7 0M6 8a8.5 8.5 0 0 1 12 0"
        stroke={COLORS.purpleLight}
        strokeWidth="1.7"
        strokeLinecap="round"
        opacity={pulse}
      />
    </svg>
  );
};

export const S3Tracking: React.FC = () => {
  return (
    <AbsoluteFill>
      <Backdrop tint="mix" />
      <AbsoluteFill style={{ padding: SAFE, justifyContent: "flex-start" }}>
        <div style={{ marginBottom: 18 }}>
          <Kicker>La solución</Kicker>
        </div>
        <Title size={82} maxWidth={1400}>
          Dos formas de seguir cada vehículo
        </Title>
        <div
          style={{
            display: "flex",
            gap: 60,
            marginTop: 70,
            justifyContent: "center",
          }}
        >
          <Card
            side="left"
            accent={COLORS.blueLight}
            tag="Modo QR"
            title="Etiqueta QR única"
            desc="El operador escanea el QR en cada etapa. La ubicación llega del teléfono."
            icon={<QrIcon />}
            delay={20}
          />
          <Card
            side="right"
            accent={COLORS.purpleLight}
            tag="Modo OBD"
            title="Dispositivo GPS"
            desc="El equipo físico reporta su posición en tiempo real, de forma continua."
            icon={<ObdIcon />}
            delay={32}
          />
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
